<script setup lang="ts">
import type { SettingsSection } from '#layers/global-settings/constants/settings-overlay-sections';
import type { BadgeProps, SidebarItem } from '#layers/ui/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOrganizationEntitlement } from '#layers/access/public';
import { SystemVersion } from '#layers/base/public';
import { useSettingsOverlay } from '#layers/global-settings/composables/use-settings-overlay';
import { DOCS_URL, FEEDBACK_URL, RELEASE_NOTES_URL, ROADMAP_URL } from '#layers/global-settings/constants/external-links';
import { isSectionVisible } from '#layers/global-settings/constants/settings-overlay-panels';
import { canViewSettingsSection } from '#layers/global-settings/constants/settings-overlay-sections';

const { t } = useI18n();
const { activeSection, navigate } = useSettingsOverlay();

const { can: canOrganization } = useOrganizationEntitlement();

function item(section: SettingsSection, icon: string, label: string, badge?: BadgeProps): SidebarItem | null {
	if (!isSectionVisible(section)) return null;

	// These items navigate via `onSelect` (no `to`), so DSidebar renders them as a plain <span>
	// without a pointer cursor — add it back. The section headers below are `hideCollapse`, so
	// they're non-interactive and should keep the default cursor.
	return { id: section, icon, label, badge, active: activeSection.value === section, onSelect: () => navigate(section), ui: { wrapper: 'cursor-pointer' } };
}

function externalItem(id: string, icon: string, label: string, url: string): SidebarItem {
	return { id, icon, label, to: url, target: '_blank' };
}

function group(id: string, label: string, children: (SidebarItem | null)[]) {
	const visibleChildren = children.filter((child) => child !== null);

	return visibleChildren.length > 0 ? [{ id, label, section: true, hideCollapse: true, class: 'cursor-default', children: visibleChildren }] : [];
}

function visibleItem(section: SettingsSection, icon: string, label: string, badge?: BadgeProps) {
	return canViewSettingsSection(section, canOrganization) ? [item(section, icon, label, badge)] : [];
}

const items = computed(() => {
	const accessItems = [
		...visibleItem('organization/api-keys', 'lucide:key-round', t('organization.dashboard.settings.access.api_keys.title')),
		...visibleItem('organization/service-accounts', 'lucide:user-cog', t('organization.dashboard.settings.access.service_accounts.title')),
		...visibleItem('organization/sso', 'lucide:shield-check', t('organization.dashboard.settings.access.providers.title')),
	];

	return [
		group('account', t('overlay.groups.account'), [
			item('account/profile', 'lucide:user', t('account.settings.profile.title')),
			item('account/access', 'lucide:shield-user', t('account.settings.access.title')),
		]),
		group('organization', t('overlay.groups.organization'), [
			...visibleItem('organization/general', 'lucide:settings', t('common.appearance')),
			item('organization/workspaces', 'lucide:layout-grid', t('organization.dashboard.workspaces.title')),
			...visibleItem('organization/extensions', 'lucide:blocks', t('extensions.title'), { color: 'warning', variant: 'subtle', label: t('common.preview') }),
			...visibleItem('organization/members', 'lucide:users', t('organization.dashboard.members.title')),
			...visibleItem('organization/audit-logs', 'lucide:logs', t('organization.dashboard.settings.access.audit_logs.title')),
			...visibleItem('organization/license', 'lucide:gem', t('organization.dashboard.settings.license.title')),
		]),
		...(accessItems.length > 0 ? [group('access', t('overlay.groups.access'), accessItems)] : []),
		group('developer', t('overlay.groups.developer'), [
			item('developer/feature-flags', 'lucide:flag', t('developer.feature_flags.title')),
		]),
		group('help', t('overlay.groups.help'), [
			externalItem('help/docs', 'lucide:book-open', t('overlay.help.docs'), DOCS_URL),
			externalItem('help/release-notes', 'lucide:rocket', t('overlay.help.release_notes'), RELEASE_NOTES_URL),
			externalItem('help/roadmap', 'lucide:map', t('overlay.help.roadmap'), ROADMAP_URL),
			externalItem('help/feedback', 'lucide:message-square-text', t('overlay.help.feedback'), FEEDBACK_URL),
		]),
		// A group whose sections are all gated off renders nothing rather than an empty heading.
	].filter((sidebarGroup) => sidebarGroup.length > 0);
});
</script>

<template>
	<DSidebar :items>
		<template #content-top>
			<SettingsOverlayHeader class="px-2 pt-1 pb-3" />
		</template>
		<template #footer>
			<SystemVersion />
		</template>
	</DSidebar>
</template>
